import React, { useState } from 'react';

import { GroupTicketResponse, TicketSyncScope } from '@ariksa/notification/api';
import { Box, Center, HStack, Link, Text } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { sum } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import {
  CustomTooltip,
  dateLabel,
  getIcon,
  getIconColor,
  renderSeverityBar,
  renderTimeStackedCell,
  StackedCell,
  WithResourceIcon,
} from 'components/DataDisplay';
import { ActionButton } from 'components/DataEntry';
import { IconTypes, RefreshIcon } from 'components/Icons';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { renderStatusOfChildTickets } from 'containers/Findings/Tickets/Components/utils';
import { selectTickets } from 'containers/Findings/Tickets/selectors';
import { actions } from 'containers/Findings/Tickets/slice';
import { TicketsSearchParamsProps } from 'containers/Findings/Tickets/types';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { useSearchParams } from 'hooks/useSearchParams';
import { toTitleCase } from 'utils/string';

export const useTicketsColumns = () => {
  const { getResourceAlias } = useResourceType();
  const { accountMapping } = useCloudAccountId();
  const [currentRecord, setCurrentRecord] = useState<GroupTicketResponse>(
    {} as GroupTicketResponse,
  );
  const { syncTicketInformation } = useSelector(selectTickets);
  const dispatch = useDispatch();
  const { environmentId } = useAccessBoundary();
  const params = useSearchParams<TicketsSearchParamsProps>();

  const handleRowSync = row => {
    dispatch(
      actions.syncTicketInformation({
        q: {
          ticketSyncRequest: {
            ticket_id: row?.ticket_id,
            client_id: row?.client_id,
            environment_id: environmentId,
            scope:
              params?.viewBy === 'findings'
                ? TicketSyncScope.GroupSingle
                : TicketSyncScope.Alert,
          },
        },
      }),
    );
  };

  const renderAction = ({ row }) => (
    <Center onClick={e => e.stopPropagation()}>
      <ActionButton
        label="Sync ticket information"
        icon={<RefreshIcon />}
        onClick={() => {
          handleRowSync(row);
          setCurrentRecord(row);
        }}
        isLoading={
          syncTicketInformation.isLoading &&
          row.ticket_id === currentRecord.ticket_id
        }
      />
    </Center>
  );

  const name = {
    header: <Box pl="thLeftPaddingWithIcon">Ticket</Box>,
    accessor: 'name',
    render: ({ row }) => (
      <WithResourceIcon
        resourceType={IconTypes.Ticket}
        iconSize="9"
        color={getIconColor(accountMapping?.[row?.account_id]?.cloud_type!)}
      >
        <StackedCell
          upper={
            <Link
              href={row?.browse_link}
              target="_blank"
              color="primary"
              textDecoration="underline"
              onClick={e => e.stopPropagation()}
            >
              {row?.ticket_id}
            </Link>
          }
          lower={toTitleCase(row?.ticket_client ?? '-')}
        />
      </WithResourceIcon>
    ),
  };

  const owner = {
    header: 'Owner',
    accessor: 'owner',
    align: 'left',
    render: ({ row }) => (
      <StackedCell
        upper={row?.assignee_name}
        lower={row?.project ?? row?.scope}
      />
    ),
  };

  const restColumns = [
    {
      header: 'Actions',
      accessor: 'actions',
      render: renderAction,
    },
  ];

  const findingsColumns = [
    name,
    owner,
    {
      header: 'Finding',
      accessor: 'finding',
      align: 'left',
      render: ({ value }) => (
        <CustomTooltip label={value}>
          <Text whiteSpace="wrap" noOfLines={2}>
            {value}
          </Text>
        </CustomTooltip>
      ),
      styles: { width: '500px', cell: { maxW: '500px' } },
    },
    {
      header: 'Child Tickets',
      accessor: 'child_ticket_status_counts',
      render: ({ value }) => sum(Object.values(value)),
    },
    {
      header: 'Status',
      align: 'left',
      render: ({ row }) => renderStatusOfChildTickets(row),
    },
    {
      header: 'Creation Date',
      accessor: 'created_at',
      align: 'left',
      render: renderTimeStackedCell,
    },
    ...restColumns,
  ];

  const issueColumns = [
    name,
    owner,
    ...(params?.viewBy === 'findings'
      ? []
      : [
          {
            header: 'Issue',
            accessor: 'issue',
            align: 'left',
            render: ({ value }) => (
              <CustomTooltip label={value}>
                <Text whiteSpace="wrap" noOfLines={2}>
                  {value}
                </Text>
              </CustomTooltip>
            ),
            styles: {
              width: '500px',
              minW: '300px',
              cell: { maxW: '500px', minW: '300px' },
            },
          },
        ]),
    {
      header: 'Resource',
      align: 'left',
      render: ({ row }) => (
        <StackedCell
          upper={row?.resource_name ?? row?.resource_id}
          lower={getResourceAlias(row?.resource_type)}
        />
      ),
      sortKey: 'resource_id',
      accessor: 'resource_id',
    },
    {
      header: 'Account',
      accessor: 'account_id',
      align: 'left',
      render: ({ value }) => (
        <StackedCell
          upper={
            accountMapping?.[value]?.name ??
            accountMapping?.[value]?.cloud_account_id
          }
          lower={
            <Box boxSize={6}>
              {getIcon(accountMapping?.[value]?.cloud_type!)}
            </Box>
          }
        />
      ),
    },
    {
      header: 'Severity',
      accessor: 'severity',
      align: 'left',
      render: ({ value }) => renderSeverityBar({ value, isInline: false }),
    },
    {
      header: 'Status',
      align: 'left',
      render: ({ row }) => toTitleCase(row?.state),
    },
    ...(params?.viewBy === 'findings'
      ? [
          {
            header: 'Created At',
            accessor: 'created_at',
            align: 'left',
            render: renderTimeStackedCell,
          },
        ]
      : []),
    {
      header: 'Due Date',
      accessor: 'due_date',
      align: 'left',
      render: ({ row }) => {
        // Tickets without a due date have no SLA; they used to render as
        // today and "Over SLA".
        if (!row?.due_date) return '-';
        const due_date = dayjs(row?.due_date);
        const slaViolation = dayjs().isAfter(due_date);
        return (
          <StackedCell
            upper={dateLabel(row?.due_date)}
            lower={
              <HStack>
                <Box
                  boxSize={3.5}
                  borderRadius={4}
                  bg={!slaViolation ? 'green.300' : 'critical'}
                />
                <Box>{!slaViolation ? 'In SLA' : 'Over SLA'}</Box>
              </HStack>
            }
          />
        );
      },
    },
    ...restColumns,
  ];
  return { findingsColumns, issueColumns, currentRecord, setCurrentRecord };
};
